import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Input, Button, Tabs, Space, message, Typography, Spin, Row, Col } from 'antd';
import {
  SaveOutlined,
  FacebookOutlined,
  GoogleOutlined,
  LinkOutlined,
  LockOutlined,
  QuestionCircleOutlined,
  AppstoreOutlined,
  TeamOutlined,
  BarChartOutlined,
  SearchOutlined,
  TagOutlined,
  LineChartOutlined,
  CodeOutlined,
  FundProjectionScreenOutlined,
} from '@ant-design/icons';
import { systemConfigAPI } from '../services/api';

const { Text, Title } = Typography;

const FB_KEYS = ['FB_APP_ID', 'FB_APP_SECRET', 'FB_ACCESS_TOKEN', 'FB_AD_ACCOUNT_ID', 'FB_PAGE_ID'];
const GADS_KEYS = ['GOOGLE_ADS_CLIENT_ID', 'GOOGLE_ADS_CLIENT_SECRET', 'GOOGLE_ADS_DEVELOPER_TOKEN', 'GOOGLE_ADS_REFRESH_TOKEN', 'GOOGLE_ADS_CUSTOMER_ID', 'GOOGLE_ADS_MANAGER_ID'];
const SECRET_KEYS = new Set(['FB_APP_SECRET', 'FB_ACCESS_TOKEN', 'GOOGLE_ADS_CLIENT_SECRET', 'GOOGLE_ADS_DEVELOPER_TOKEN', 'GOOGLE_ADS_REFRESH_TOKEN']);

const FB_FIELDS = [
  { key: 'FB_APP_ID', label: 'App ID', hint: '輸入 Facebook App ID', icon: <AppstoreOutlined /> },
  { key: 'FB_APP_SECRET', label: 'App Secret', hint: '輸入 Facebook App Secret', icon: <LockOutlined /> },
  { key: 'FB_ACCESS_TOKEN', label: '長期存取權杖', hint: '輸入 Long-lived Access Token', icon: <LockOutlined /> },
  { key: 'FB_AD_ACCOUNT_ID', label: '廣告帳號 ID', hint: '格式：act_xxxxxxxxx', icon: <FundProjectionScreenOutlined /> },
  { key: 'FB_PAGE_ID', label: '粉絲專頁 ID', hint: '輸入粉絲專頁 ID', icon: <TeamOutlined /> },
];

const GADS_FIELDS = [
  { key: 'GOOGLE_ADS_CLIENT_ID', label: 'OAuth Client ID', hint: '輸入 OAuth 2.0 Client ID', icon: <AppstoreOutlined /> },
  { key: 'GOOGLE_ADS_CLIENT_SECRET', label: 'OAuth Client Secret', hint: '輸入 OAuth Client Secret', icon: <LockOutlined /> },
  { key: 'GOOGLE_ADS_DEVELOPER_TOKEN', label: 'Developer Token', hint: '輸入 Google Ads Developer Token', icon: <LockOutlined /> },
  { key: 'GOOGLE_ADS_REFRESH_TOKEN', label: 'Refresh Token', hint: '輸入 OAuth Refresh Token', icon: <LockOutlined /> },
  { key: 'GOOGLE_ADS_CUSTOMER_ID', label: '客戶 ID', hint: '格式：xxx-xxx-xxxx', icon: <TagOutlined /> },
  { key: 'GOOGLE_ADS_MANAGER_ID', label: '管理員帳戶 ID（選填）', hint: 'MCC 帳戶 ID（選填）', icon: <TeamOutlined /> },
];

const FB_LINKS = [
  { label: 'Ads Manager', url: 'https://www.facebook.com/adsmanager', icon: <FundProjectionScreenOutlined /> },
  { label: 'Business Suite', url: 'https://business.facebook.com', icon: <AppstoreOutlined /> },
  { label: '粉絲專頁管理', url: 'https://www.facebook.com/pages', icon: <TeamOutlined /> },
  { label: '受眾洞察', url: 'https://www.facebook.com/ads/audience-insights', icon: <BarChartOutlined /> },
];

const GADS_LINKS = [
  { label: 'Google Ads', url: 'https://ads.google.com', icon: <FundProjectionScreenOutlined /> },
  { label: 'Keyword Planner', url: 'https://ads.google.com/aw/keywordplanner', icon: <SearchOutlined /> },
  { label: 'Google Analytics', url: 'https://analytics.google.com', icon: <LineChartOutlined /> },
  { label: 'Google Tag Manager', url: 'https://tagmanager.google.com', icon: <CodeOutlined /> },
];

const FB_STEPS = [
  '前往 developers.facebook.com 建立應用程式',
  '取得 App ID 和 App Secret',
  '在「行銷 API」中產生長期存取權杖',
  '在 Ads Manager 中找到廣告帳號 ID（act_ 開頭）',
  '在粉絲專頁「關於」中找到專頁 ID',
];

const GADS_STEPS = [
  '前往 console.cloud.google.com 建立專案',
  '啟用 Google Ads API',
  '建立 OAuth 2.0 憑證取得 Client ID / Secret',
  '前往 ads.google.com/aw/apicenter 取得 Developer Token',
  '使用 OAuth 流程取得 Refresh Token',
  '在 Google Ads 帳戶設定中找到客戶 ID',
];

function QuickLinks({ links, color }) {
  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Row gutter={[12, 12]}>
        {links.map((link) => (
          <Col xs={12} sm={12} key={link.label}>
            <Button
              type="default"
              icon={link.icon}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              block
              style={{ textAlign: 'left', borderColor: color, color }}
            >
              {link.label}
              <LinkOutlined style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.5 }} />
            </Button>
          </Col>
        ))}
      </Row>
    </Card>
  );
}

function HelpSteps({ steps }) {
  return (
    <Card
      size="small"
      style={{ background: '#f6f8fb', border: '1px solid #e8ecf1' }}
      title={<Space><QuestionCircleOutlined /> 設定說明</Space>}
    >
      <ol style={{ paddingLeft: 20, margin: 0 }}>
        {steps.map((step, i) => (
          <li key={i} style={{ marginBottom: 6, color: '#555', fontSize: 13 }}>{step}</li>
        ))}
      </ol>
    </Card>
  );
}

function MarketingToolsPage() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await systemConfigAPI.list();
      const map = {};
      (res.data || []).forEach((c) => { map[c.key] = c; });
      setConfigs(map);
      const formValues = {};
      [...FB_KEYS, ...GADS_KEYS].forEach((key) => {
        formValues[key] = map[key]?.value || '';
      });
      form.setFieldsValue(formValues);
    } catch (error) {
      message.error('載入設定失敗: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = form.getFieldsValue();
      const configList = Object.entries(values).map(([key, value]) => ({
        key,
        value: value || '',
      }));
      await systemConfigAPI.update({ configs: configList });
      message.success('行銷工具設定已儲存');
      fetchConfigs();
    } catch (error) {
      message.error('儲存失敗: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const renderFields = (fields) => (
    fields.map((field) => (
      <Form.Item
        key={field.key}
        name={field.key}
        label={
          <Space>
            {field.icon}
            <span>{field.label}</span>
            {configs[field.key]?.description && (
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                — {configs[field.key].description}
              </Text>
            )}
          </Space>
        }
      >
        {SECRET_KEYS.has(field.key) ? (
          <Input.Password placeholder={field.hint} />
        ) : (
          <Input placeholder={field.hint} />
        )}
      </Form.Item>
    ))
  );

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
  }

  const tabItems = [
    {
      key: 'facebook',
      label: (
        <span>
          <FacebookOutlined style={{ marginRight: 6 }} />
          Facebook 行銷
        </span>
      ),
      children: (
        <div>
          <Title level={5} style={{ color: '#1877F2', marginBottom: 16 }}>
            <FacebookOutlined style={{ marginRight: 8 }} />
            Facebook 行銷工具
          </Title>
          <QuickLinks links={FB_LINKS} color="#1877F2" />

          <Card
            title={<Space><LockOutlined /> Facebook API 設定</Space>}
            size="small"
            style={{ marginBottom: 16 }}
          >
            {renderFields(FB_FIELDS)}
          </Card>

          <HelpSteps steps={FB_STEPS} />
        </div>
      ),
    },
    {
      key: 'google-ads',
      label: (
        <span>
          <GoogleOutlined style={{ marginRight: 6 }} />
          Google Ads
        </span>
      ),
      children: (
        <div>
          <Title level={5} style={{ color: '#4285F4', marginBottom: 16 }}>
            <GoogleOutlined style={{ marginRight: 8 }} />
            Google Ads 行銷工具
          </Title>
          <QuickLinks links={GADS_LINKS} color="#4285F4" />

          <Card
            title={<Space><LockOutlined /> Google Ads API 設定</Space>}
            size="small"
            style={{ marginBottom: 16 }}
          >
            {renderFields(GADS_FIELDS)}
          </Card>

          <HelpSteps steps={GADS_STEPS} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <FundProjectionScreenOutlined />
            行銷工具設定
          </Space>
        }
        extra={
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
            儲存設定
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Tabs items={tabItems} />
        </Form>
      </Card>
    </div>
  );
}

export default MarketingToolsPage;
